import localForageHelper from '@/helpers/localforage.helper'
import onConfigChange from '@/observables/config.obvervable'
import SelectorComponent from '@components/inputs/selector/selector.component'
import MySquid from '@interfaces/my-squid.interface'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Subscription, BehaviorSubject } from 'rxjs'
import './toggle-theme.component.scss'
import { SqComponentWithExternalScopedCss } from '@squidit/react-css'

export const ThemeObservable = new BehaviorSubject<string>('light')

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  labelPosition?: 'left' | 'right' | 'none'
  fontSize?: string
  externalCssScopedHash?: string
}

export default ({ labelPosition = 'left', className = '', fontSize = '1rem', externalCssScopedHash }: Props) => {
  const onConfigChangeSubscription = useRef<Subscription>(undefined)
  const [configs, setConfig] = useState<MySquid>(null)
  const [checked, setChecked] = useState(window.matchMedia('(prefers-color-scheme:dark)').matches)
  const { t } = useTranslation('toggleTheme')

  const handleChange = useCallback(
    async (thisCheck: boolean) => {
      const newTheme = thisCheck ? 'dark' : 'light'
      const html = document.getElementsByTagName('html')[0]
      html.classList.value = newTheme
      await localForageHelper.setItem('theme', newTheme)
      ThemeObservable.next(newTheme)
      setChecked(thisCheck)
    },
    [setChecked]
  )

  useEffect(() => {
    onConfigChangeSubscription.current = onConfigChange.subscribe((configChanged: MySquid) => {
      if (configChanged?._id) {
        setConfig(configChanged)
      }
      if (!configs?._id || !configs.theme.theme || configs.theme.theme === 'both') {
        localForageHelper.getItem('theme').then((theme) => {
          if (theme) {
            handleChange(theme === 'dark')
          }
        })
      }
    })
    return () => {
      onConfigChangeSubscription?.current?.unsubscribe()
    }
  }, [onConfigChangeSubscription, handleChange, configs])

  if (configs?._id && configs.theme.theme && configs.theme.theme !== 'both') {
    return null
  }

  return (
    <SqComponentWithExternalScopedCss hash={externalCssScopedHash}>
      <SelectorComponent
        className={'theme-selector ' + className}
        errorSpan={false}
        checked={checked}
        onChange={(c: boolean) => handleChange(c)}
        toggle
        name="hub-select-theme"
        id="hub-select-theme"
        labelFontSize={fontSize}
        rightLabel={labelPosition === 'right' ? t('label') : null}
        leftLabel={labelPosition === 'left' ? t('label') : null}
        externalCssScopedHash={externalCssScopedHash}
      />
    </SqComponentWithExternalScopedCss>
  )
}
